import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Form, Input, Select, Button, Space } from 'antd';
import { SaveOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, GiftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { message } from 'antd';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { holidaysApi, departmentsApi, designationsApi } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';

const { Option } = Select;
const { TextArea } = Input;

const AddHoliday = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [holidayEntries, setHolidayEntries] = useState([{ id: 1, date: null, occasion: '' }]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [deptRes, desRes] = await Promise.all([
          departmentsApi.getAll(),
          designationsApi.getAll(),
        ]);
        setDeptOptions(deptRes.data || []);
        setDesignationOptions(desRes.data || []);
      } catch (error) {
        console.error('خطا در دریافت داده‌ها:', error);
        message.error('خطا در دریافت داده‌های مورد نیاز');
      }
    };
    loadOptions();
  }, []);

  const handleAddEntry = () => {
    setHolidayEntries([...holidayEntries, { id: Date.now(), date: null, occasion: '' }]);
  };

  const handleRemoveEntry = (id) => {
    if (holidayEntries.length > 1) {
      setHolidayEntries(holidayEntries.filter((entry) => entry.id !== id));
    } else {
      message.warning('حداقل یک ردیف باید وجود داشته باشد');
    }
  };

  const formatDate = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString().slice(0, 10);
    } catch (e) {
      return null;
    }
  };

  const handleSubmit = async (values) => {
    try {
      const validatedValues = await form.validateFields();
      setLoading(true);
      // ارسال هر ردیف به صورت جداگانه طبق ساختار API
      for (const entry of holidayEntries) {
        const payload = {
          occassion: validatedValues[`occasion_${entry.id}`] || '',
          date: formatDate(validatedValues[`date_${entry.id}`]),
          department: validatedValues.department ? validatedValues.department.map(Number) : [],
          designation: validatedValues.jobTitle ? validatedValues.jobTitle.map(Number) : [],
          employment_type: validatedValues.employmentType || [],
        };
        await holidaysApi.create(payload);
      }
      message.success('تعطیلات با موفقیت ثبت شد');
      navigate('/hr/holidays');
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else {
        message.error('خطا در ثبت تعطیلات');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/hr/holidays');
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                <GiftOutlined className="text-amber-600 dark:text-amber-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">افزودن تعطیلات</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">تاریخ‌ها و مناسبت‌های تعطیلی را ثبت کنید</p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/hr/holidays')} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6">
              <Form form={form} layout="vertical" onFinish={handleSubmit} scrollToFirstError={{ behavior: 'smooth', block: 'center' }} style={{ fontFamily: 'CustomFont' }}>
                <div className="mb-6">
                  {holidayEntries.map((entry) => (
                    <div key={entry.id} className="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900/40">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Form.Item
                            label={
                              <span>
                                تاریخ
                              </span>
                            }
                            name={`date_${entry.id}`}
                            rules={[{ required: true, message: 'لطفا تاریخ را انتخاب کنید' }]}
                          >
                            <PersianDatePicker style={{ width: '100%' }} placeholder="انتخاب تاریخ" />
                          </Form.Item>

                          <Form.Item label={<span>مناسبت</span>} name={`occasion_${entry.id}`}>
                            <TextArea rows={2} placeholder="مناسبت" style={{ fontFamily: 'CustomFont' }} />
                          </Form.Item>
                        </div>

                        {holidayEntries.length > 1 && (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveEntry(entry.id)}
                            className="mt-8"
                          />
                        )}
                      </div>
                    </div>
                  ))}

                  <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddEntry} block style={{ fontFamily: 'CustomFont' }}>
                    افزودن ردیف تعطیلی
                  </Button>
                </div>

                <Card
                  className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 mb-6"
                  title={<span style={{ fontFamily: 'CustomFont' }}>فیلترهای اعمال تعطیلات</span>}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Form.Item label="دپارتمان" name="department">
                      <Select
                        mode="multiple"
                        placeholder="انتخاب دپارتمان"
                        allowClear
                        style={{ fontFamily: 'CustomFont' }}
                        loading={!deptOptions.length}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          if (option?.value === '__all__') return true;
                          return (option?.children ?? '').toLowerCase().includes(input.toLowerCase());
                        }}
                        onChange={(values) => {
                          if (values.includes('__all__')) {
                            form.setFieldsValue({ department: deptOptions.map((d) => String(d.id)) });
                          }
                        }}
                      >
                        <Option value="__all__"><span style={{ fontWeight: 'bold', color: '#1890ff' }}>— انتخاب همه —</span></Option>
                        {deptOptions.map((d) => (
                          <Option key={d.id} value={String(d.id)}>
                            {d.team_name || d.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item label="عنوان شغلی" name="jobTitle">
                      <Select
                        mode="multiple"
                        placeholder="انتخاب عنوان شغلی"
                        allowClear
                        style={{ fontFamily: 'CustomFont' }}
                        loading={!designationOptions.length}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          if (option?.value === '__all__') return true;
                          return (option?.children ?? '').toLowerCase().includes(input.toLowerCase());
                        }}
                        onChange={(values) => {
                          if (values.includes('__all__')) {
                            form.setFieldsValue({ jobTitle: designationOptions.map((d) => String(d.id)) });
                          }
                        }}
                      >
                        <Option value="__all__"><span style={{ fontWeight: 'bold', color: '#1890ff' }}>— انتخاب همه —</span></Option>
                        {designationOptions.map((d) => (
                          <Option key={d.id} value={String(d.id)}>
                            {d.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item label="نوع استخدام" name="employmentType">
                      <Select
                        mode="multiple"
                        placeholder="انتخاب نوع استخدام"
                        allowClear
                        style={{ fontFamily: 'CustomFont' }}
                        onChange={(values) => {
                          if (values.includes('__all__')) {
                            form.setFieldsValue({ employmentType: ['fulltime', 'parttime', 'contract', 'intern', 'training'] });
                          }
                        }}
                      >
                        <Option value="__all__"><span style={{ fontWeight: 'bold', color: '#1890ff' }}>— انتخاب همه —</span></Option>
                        <Option value="fulltime">تمام وقت</Option>
                        <Option value="parttime">پاره وقت</Option>
                        <Option value="contract">قراردادی</Option>
                        <Option value="intern">کارآموزی</Option>
                        <Option value="training">آموزشی</Option>
                      </Select>
                    </Form.Item>
                  </div>
                </Card>

                <div className="flex items-center justify-end gap-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <Button icon={<CloseOutlined />} onClick={handleCancel} size="middle" style={{ fontFamily: 'CustomFont' }}>
                    لغو
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                    size="middle"
                    className="bg-green-600 hover:bg-green-700 border-green-600"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    ذخیره
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddHoliday;
